import * as Analytics from 'analytics/Analytics';
import {structuredTableActionOrigin} from 'analytics/Util';
import {assert} from 'Asserts';
import {DataModificationsHeader} from 'audit/DataModifications';
import {ELN_ANALYTICS_ACTIONS} from 'constants/analytics-products/ElnAnalyticsConstants';
import {callSampleStatusWarningModal} from 'inventory/sample-status/SampleStatusWarningModal';
import {ACTION_SUBMIT_INVENTORY_ROW} from 'notebook/store/VersioningConstants';
import {
  doPerformInventorySubmit,
  getSampleIdsForStatusWarningFromStructuredTable,
} from 'notebook/structured-tables/action-functions/inventorySubmitActionFunction';
import makeSubmitRowActionFunction, {
  getRowAwareLoadingMessage,
} from 'notebook/structured-tables/action-functions/makeSubmitRowActionFunction';
import * as ActionFunctionDAO from 'notebook/structured-tables/ActionFunctionDAO';
import invokeStructuredTableMappings from 'notebook/structured-tables/structured-table-mappings/invokeStructuredTableMappings';
import {AxisContextMenuActionFunctionParameters} from 'notebook/structured-tables/StructuredTableConstants';
import {InventoryTableMetadata} from 'notebook/structured-tables/StructuredTableConstants';
import {TableSubmissionDiffs} from 'notebook/structured-tables/util/datamods/getStructuredTableCellDiffOnSubmission';
import {isInExecutableCanvas} from 'notebook/structured-tables/util/isInExecutableCanvas';
import {
  ActionResultType,
  performServerSideTableWriteActionWithAudits,
} from 'notebook/util/PerformActionFunctions';

import {InventoryStructuredTable, InventoryTableRowMetadata} from './InventoryStructuredTableAdapter';

export const SUBMIT_INVENTORY_ROW_ACTION_FUNCTION_CONFIG = {
  allowOutsideOfEntry: false,
  onSelectForSubmit: async (
    actionFunctionParams: AxisContextMenuActionFunctionParameters<
      InventoryTableMetadata,
      InventoryTableRowMetadata
    >
  ) => {
    assert(
      isInExecutableCanvas(actionFunctionParams),
      'Inventory submit action is only supported in entries and executable documents.',
      {apiId: actionFunctionParams.entryOrTemplateId}
    );
    const entryId = actionFunctionParams.entryOrTemplateId;
    assert(entryId != null, 'Document ID not provided');
    const inventoryStructuredTable: InventoryStructuredTable = actionFunctionParams.structuredTable;
    const selection = {
      start: actionFunctionParams.selectionRange!.minIndex,
      end: actionFunctionParams.selectionRange!.maxIndex + 1,
    };

    const structuredTableRows = selection
      ? inventoryStructuredTable.rows.slice(selection.start, selection.end)
      : inventoryStructuredTable.rows;
    const slicedComputedTable = selection
      ? actionFunctionParams.computedTable.slice(selection.start, selection.end)
      : actionFunctionParams.computedTable;

    const sampleIds = getSampleIdsForStatusWarningFromStructuredTable({
      structuredTableRows,
      structuredTableColumns: inventoryStructuredTable.columns,
      structuredTableSchemas: inventoryStructuredTable.schemas,
      computedTable: slicedComputedTable,
    });
    const {shouldProceed} = await callSampleStatusWarningModal({
      sampleIds: [...new Set(sampleIds)],
      trackingParams: {
        actionOrigin: structuredTableActionOrigin(inventoryStructuredTable),
        actionAttempted: 'update_quantity',
        eventGroupId: Analytics.makeAnalyticsEventId(),
      },
      canSelectMultiple: false,
      showOnlyWarnings: true,
    });

    if (shouldProceed) {
      await performServerSideTableWriteActionWithAudits<ActionFunctionDAO.InventorySubmitResponse>({
        entryId,
        loadingScreenMessage: getRowAwareLoadingMessage(selection, 'Updating Rows', 'Updating Row'),
        autoSaverAction: ACTION_SUBMIT_INVENTORY_ROW,
        doPerformAction: (
          tableSubmissionDiffs?: TableSubmissionDiffs,
          updateStatus?: (msg: string) => void,
          dataModsHeader?: DataModificationsHeader
        ) =>
          doPerformInventorySubmit(
            entryId,
            inventoryStructuredTable,
            actionFunctionParams.computedTable,
            actionFunctionParams.actionFunctionContext,
            tableSubmissionDiffs,
            selection,
            updateStatus,
            dataModsHeader
          ),
        doPostRefresh: (actionResult) => {
          invokeStructuredTableMappings(
            entryId,
            inventoryStructuredTable,
            actionResult.resultType === ActionResultType.PARTIAL_SUCCESS
              ? actionResult.submittedRange
              : selection
          );
          return Promise.resolve();
        },
        analyticsActionForPerfTracking:
          ELN_ANALYTICS_ACTIONS.UPDATED_CONTAINERS_OR_WELLS_FROM_INVENTORY_TABLE_ROWS.action,
        structuredTables: [actionFunctionParams.structuredTable],
        structuredTableAdapters: [actionFunctionParams.structuredTableAdapter],
        computedTables: [actionFunctionParams.computedTable],
        tables: actionFunctionParams.table ? [actionFunctionParams.table] : [],
        selection,
      });
    }
  },
};

export default makeSubmitRowActionFunction<InventoryTableMetadata, InventoryTableRowMetadata>(
  SUBMIT_INVENTORY_ROW_ACTION_FUNCTION_CONFIG
);
